import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { C } from '../../constants/colors.js';
import { F, N } from '../../constants/fonts.js';
import { STRIPE_PK } from '../../constants/stripe.js';
import finleyLogo from '../../assets/finley-logo-full-512.png';
import { supaGetSession, supaSignInWithGoogle, supaSignUp } from '../../lib/supabase.js';
import { saveParentEmail } from '../../lib/email.js';
import { screenTransition } from '../../lib/motionPresets.js';
import { _btnPress, _btnRelease, _NBtn } from '../onboarding/Onboarding.jsx';
import { ReadingStageCheck } from './ReadingStageCheck.jsx';
import { PersonalizedPreview } from './PersonalizedPreview.jsx';

const STEPS = {
  WELCOME: 'welcome',
  AGE: 'age',
  READING_CHECK: 'reading_check',
  CONCERNS_GOALS: 'concerns_goals',
  PREVIEW: 'preview',
  AUTH_PAYMENT: 'auth_payment',
  CHILD_PROFILE: 'child_profile',
  HERO_NAME: 'hero_name',
  PIN: 'pin',
};

const STEP_ORDER = [
  STEPS.WELCOME,
  STEPS.AGE,
  STEPS.READING_CHECK,
  STEPS.CONCERNS_GOALS,
  STEPS.PREVIEW,
  STEPS.AUTH_PAYMENT,
  STEPS.CHILD_PROFILE,
  STEPS.HERO_NAME,
  STEPS.PIN,
];

const AGE_OPTIONS = [
  { id: '4-5', label: '4-5', detail: 'Pre-Reader' },
  { id: '6-7', label: '6-7', detail: 'Early Reader' },
  { id: '8-9', label: '8-9', detail: 'Growing Reader' },
  { id: '10-12', label: '10-12', detail: 'Fluent Reader' },
];

const HEROES = [
  {
    id: 'Finn',
    emoji: '🐬',
    color: '#0D9488',
    bgGradient: 'linear-gradient(135deg, #E0F7F5 0%, #B2DFDB 100%)',
    description: 'A curious dolphin who loves diving into new stories',
  },
  {
    id: 'Blaze',
    emoji: '🔥',
    color: '#E8614D',
    bgGradient: 'linear-gradient(135deg, #FFE8E0 0%, #FFCCBC 100%)',
    description: 'A fearless explorer who races through reading challenges',
  },
  {
    id: 'Nova',
    emoji: '⭐',
    color: '#7C3AED',
    bgGradient: 'linear-gradient(135deg, #EDE9FE 0%, #DDD6FE 100%)',
    description: 'A star voyager who discovers new words across galaxies',
  },
  {
    id: 'Reef',
    emoji: '🐢',
    color: '#0EA5E9',
    bgGradient: 'linear-gradient(135deg, #E0F2FE 0%, #BAE6FD 100%)',
    description: 'A wise sea turtle who collects stories from the ocean',
  },
  {
    id: 'Bolt',
    emoji: '🐆',
    color: '#F59E0B',
    bgGradient: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)',
    description: 'A speedy leopard who zooms through sight words',
  },
  {
    id: 'Luna',
    emoji: '🦉',
    color: '#6366F1',
    bgGradient: 'linear-gradient(135deg, #E0E7FF 0%, #C7D2FE 100%)',
    description: 'A dreamy owl who reads under the moonlight',
  },
];

const INTEREST_OPTIONS = [
  {
    id: 'ocean',
    label: 'Ocean',
    emoji: '🌊',
    color: '#0EA5E9',
    bgGradient: 'linear-gradient(135deg, #E0F2FE 0%, #BAE6FD 100%)',
    description: 'Underwater worlds with coral reefs, sea creatures, and sunken treasure hunts',
  },
  {
    id: 'space',
    label: 'Space',
    emoji: '🚀',
    color: '#7C3AED',
    bgGradient: 'linear-gradient(135deg, #EDE9FE 0%, #DDD6FE 100%)',
    description: 'Rocket ships, distant planets, alien friends, and cosmic discoveries',
  },
  {
    id: 'dinos',
    label: 'Dinos',
    emoji: '🦕',
    color: '#16A34A',
    bgGradient: 'linear-gradient(135deg, #DCFCE7 0%, #BBF7D0 100%)',
    description: 'Prehistoric jungles, dino friends, fossil hunts, and volcanic adventures',
  },
  {
    id: 'robots',
    label: 'Robots',
    emoji: '🤖',
    color: '#F59E0B',
    bgGradient: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)',
    description: 'Invention labs, friendly robots, gadgets, and futuristic missions',
  },
  {
    id: 'fantasy',
    label: 'Fantasy',
    emoji: '🧙',
    color: '#6366F1',
    bgGradient: 'linear-gradient(135deg, #E0E7FF 0%, #C7D2FE 100%)',
    description: 'Magic spells, enchanted forests, dragons, and quest-filled kingdoms',
  },
  {
    id: 'sports',
    label: 'Sports',
    emoji: '⚽',
    color: '#E8614D',
    bgGradient: 'linear-gradient(135deg, #FFE8E0 0%, #FFCCBC 100%)',
    description: 'Big games, championship moments, teamwork, and training montages',
  },
];

const CONCERN_OPTIONS = [
  'Struggles to sound out new words',
  'Reads slowly or word-by-word',
  "Forgets words they've seen before",
  "Doesn't enjoy reading",
  'Behind where they should be',
  'Supplement school or homeschool',
  'Loves reading — wants to go further',
  'Just exploring',
];

const GOAL_OPTIONS = [
  'Reads independently for fun',
  'At or above grade level',
  'Confident reading aloud',
  'Discovers books they love',
];

const AGE_BRACKET_TO_AGE = {
  '4-5': 5,
  '6-7': 7,
  '8-9': 9,
  '10-12': 11,
};

const TEAL = C.teal || C.secondary;
const NAVY = C.navy || C.text;
const WARM_WHITE = C.warmWhite || C.bg;

function isStep(value) {
  return STEP_ORDER.includes(value);
}

function FlipCard({ item, selected, onToggle }) {
  const [flipped, setFlipped] = useState(false);

  const handleTap = () => {
    setFlipped((f) => !f);
    onToggle(item.id);
  };

  return (
    <motion.div
      whileTap={{ scale: 0.95 }}
      onClick={handleTap}
      style={{
        perspective: 600,
        cursor: 'pointer',
        height: 110,
      }}
    >
      <motion.div
        animate={{ rotateY: flipped ? 180 : 0 }}
        transition={{ duration: 0.4, ease: 'easeInOut' }}
        style={{
          width: '100%',
          height: '100%',
          position: 'relative',
          transformStyle: 'preserve-3d',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backfaceVisibility: 'hidden',
            borderRadius: 16,
            border: `2px solid ${selected ? item.color : '#E5EBF0'}`,
            background: selected ? item.bgGradient : '#FFFFFF',
            padding: '14px 10px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: selected ? `0 4px 12px ${item.color}20` : 'none',
            transition: 'border-color 0.2s, box-shadow 0.2s',
          }}
        >
          <div style={{ fontSize: 28, marginBottom: 4 }}>{item.emoji}</div>
          <div
            style={{
              fontFamily: F,
              fontSize: 14,
              color: selected ? item.color : NAVY,
              fontWeight: 700,
            }}
          >
            {item.label}
          </div>
        </div>

        <div
          style={{
            position: 'absolute',
            inset: 0,
            backfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
            borderRadius: 16,
            border: `2px solid ${item.color}`,
            background: item.bgGradient,
            padding: '12px 10px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              fontFamily: F,
              fontSize: 13,
              color: item.color,
              fontWeight: 700,
              marginBottom: 4,
            }}
          >
            {item.emoji} {item.label}
          </div>
          <div
            style={{
              fontFamily: N,
              fontSize: 12,
              color: 'rgba(15,43,60,0.75)',
              lineHeight: 1.35,
            }}
          >
            {item.description}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

function HeroFlipCard({ hero, selected, onSelect }) {
  const [flipped, setFlipped] = useState(false);

  const handleTap = () => {
    setFlipped((f) => !f);
    onSelect(hero.id);
  };

  return (
    <motion.div
      whileTap={{ scale: 0.95 }}
      onClick={handleTap}
      style={{
        perspective: 600,
        cursor: 'pointer',
        height: 132,
      }}
    >
      <motion.div
        animate={{ rotateY: flipped ? 180 : 0 }}
        transition={{ duration: 0.4, ease: 'easeInOut' }}
        style={{
          width: '100%',
          height: '100%',
          position: 'relative',
          transformStyle: 'preserve-3d',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backfaceVisibility: 'hidden',
            borderRadius: 16,
            border: `2px solid ${selected ? hero.color : '#E5EBF0'}`,
            background: selected ? hero.bgGradient : '#FFFFFF',
            padding: '14px 12px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            boxShadow: selected ? `0 4px 14px ${hero.color}25` : 'none',
            transition: 'border-color 0.2s, box-shadow 0.2s',
          }}
        >
          <div style={{ fontSize: 28, marginBottom: 4 }}>{hero.emoji}</div>
          <div
            style={{
              fontFamily: F,
              fontSize: 16,
              color: selected ? hero.color : NAVY,
              fontWeight: 700,
            }}
          >
            {hero.id}
          </div>
        </div>

        <div
          style={{
            position: 'absolute',
            inset: 0,
            backfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
            borderRadius: 16,
            border: `2px solid ${hero.color}`,
            background: hero.bgGradient,
            padding: '12px 10px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              fontFamily: F,
              fontSize: 13,
              color: hero.color,
              fontWeight: 700,
              marginBottom: 4,
            }}
          >
            {hero.emoji} {hero.id}
          </div>
          <div
            style={{
              fontFamily: N,
              fontSize: 12,
              color: 'rgba(15,43,60,0.75)',
              lineHeight: 1.35,
            }}
          >
            {hero.description}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

export function ParentSetup({ onComplete, onAuthChange, authUser: authUserProp }) {
  const isDevSkipEnabled = import.meta.env.DEV;
  const containerRef = useRef(null);
  const [step, setStep] = useState(STEPS.WELCOME);
  const [hydrated, setHydrated] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  const [onboardingData, setOnboardingData] = useState({
    ageBracket: null,
    readingChecks: [],
    readingStage: null,
    concerns: [],
    goal: null,
    childName: '',
    childAge: null,
    heroName: '',
    interests: [],
    pin: null,
  });

  const [authUser, setAuthUser] = useState(authUserProp || null);
  const [sEmail, setSEmail] = useState('');
  const [sPw, setSPw] = useState('');
  const [sErr, setSErr] = useState('');
  const [sBusy, setSBusy] = useState(false);
  const [sDone, setSDone] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);
  const [pwFocused, setPwFocused] = useState(false);
  const [childNameFocused, setChildNameFocused] = useState(false);
  const [childAgeFocused, setChildAgeFocused] = useState(false);

  const [stripeLoading, setStripeLoading] = useState(false);
  const [stripeErr, setStripeErr] = useState('');
  const [stripeReady, setStripeReady] = useState(false);
  const [stripeEl, setStripeEl] = useState(null);
  const cardRef = useRef(null);
  const stripeCardRef = useRef(null);

  const [pinSetup, setPinSetup] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [confirmStep, setConfirmStep] = useState(false);
  const [pinError, setPinError] = useState('');

  const updateData = (patch) => setOnboardingData((prev) => ({ ...prev, ...patch }));

  useEffect(() => {
    try {
      const done = localStorage.getItem('rq:parentSetupDone');
      if (!done) {
        const savedData = localStorage.getItem('rq:onboardingData');
        if (savedData) {
          const parsed = JSON.parse(savedData);
          if (parsed && typeof parsed === 'object') {
            setOnboardingData((prev) => ({ ...prev, ...parsed }));
          }
        }

        const pendingOauth = localStorage.getItem('rq:oauthPending');
        const savedStep = localStorage.getItem('rq:onboardingStep');

        if (pendingOauth) {
          localStorage.removeItem('rq:oauthPending');
          if (isStep(pendingOauth)) {
            setStep(pendingOauth);
          } else if (isStep(savedStep)) {
            setStep(savedStep);
          } else {
            setStep(STEPS.AUTH_PAYMENT);
          }
        } else if (isStep(savedStep)) {
          setStep(savedStep);
        }
      }
    } catch {
      // noop
    }
    setHydrated(true);
  }, []);

  useEffect(() => {
    setAuthUser(authUserProp || null);
    if (authUserProp?.email) {
      setSEmail(authUserProp.email);
    }
  }, [authUserProp]);

  useEffect(() => {
    supaGetSession()
      .then((session) => {
        if (session?.user) {
          setAuthUser(session.user);
          if (session.user.email) {
            setSEmail(session.user.email);
            saveParentEmail(session.user.email);
          }
          if (onAuthChange) onAuthChange(session.user);
        }
      })
      .finally(() => setAuthChecked(true));
  }, [onAuthChange]);

  useEffect(() => {
    if (!hydrated) return;
    try {
      if (!localStorage.getItem('rq:parentSetupDone')) {
        localStorage.setItem('rq:onboardingStep', step);
        localStorage.setItem('rq:onboardingData', JSON.stringify(onboardingData));
      }
    } catch {
      // noop
    }
  }, [step, onboardingData, hydrated]);

  useEffect(() => {
    const resetScrollTop = () => {
      const node = containerRef.current;
      if (node) {
        node.scrollTop = 0;
        node.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      }
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };

    resetScrollTop();
    const frameId = requestAnimationFrame(resetScrollTop);
    const timeoutId = setTimeout(resetScrollTop, 220);

    return () => {
      cancelAnimationFrame(frameId);
      clearTimeout(timeoutId);
    };
  }, [step]);

  useEffect(() => {
    if (step !== STEPS.AUTH_PAYMENT || stripeCardRef.current || !cardRef.current) return;
    try {
      if (!window.Stripe) {
        setStripeReady(true);
        return;
      }
      const stripeInstance = window.Stripe(STRIPE_PK);
      const elements = stripeInstance.elements();
      const card = elements.create('card', {
        style: {
          base: {
            fontSize: '17px',
            fontFamily: N,
            color: '#1A1A2E',
            '::placeholder': { color: '#9CA3AF' },
          },
          invalid: { color: '#E8614D' },
        },
      });
      card.mount(cardRef.current);
      card.on('ready', () => setStripeReady(true));
      card.on('change', (e) => {
        if (e.error) setStripeErr(e.error.message);
        else setStripeErr('');
      });
      stripeCardRef.current = card;
      setStripeEl(stripeInstance);
    } catch {
      setStripeReady(true);
    }
  }, [step]);

  const isAuthed = !!authUser;
  const currentStepIndex = STEP_ORDER.indexOf(step);
  const progressPct = useMemo(() => {
    if (currentStepIndex <= 0) return 0;
    return (currentStepIndex / (STEP_ORDER.length - 1)) * 100;
  }, [currentStepIndex]);

  const goBack = () => {
    if (step === STEPS.WELCOME) return;
    if (step === STEPS.PIN && confirmStep) {
      setConfirmStep(false);
      setConfirmPin('');
      setPinError('');
      return;
    }
    const i = STEP_ORDER.indexOf(step);
    if (i > 0) setStep(STEP_ORDER[i - 1]);
  };

  const toggleConcern = (concern) => {
    const next = onboardingData.concerns.includes(concern)
      ? onboardingData.concerns.filter((x) => x !== concern)
      : [...onboardingData.concerns, concern];
    updateData({ concerns: next });
  };

  const toggleInterest = (id) => {
    const has = onboardingData.interests.includes(id);
    const next = has
      ? onboardingData.interests.filter((x) => x !== id)
      : onboardingData.interests.length < 3
        ? [...onboardingData.interests, id]
        : onboardingData.interests;
    updateData({ interests: next });
  };

  const doCreate = async () => {
    if (!sEmail || !sEmail.includes('@')) {
      setSErr('Please enter a valid email address.');
      return;
    }
    if (sPw.length < 6) {
      setSErr('Password must be at least 6 characters.');
      return;
    }

    setSBusy(true);
    setSErr('');
    try {
      const { data, error } = await supaSignUp(sEmail.trim(), sPw);
      if (error) {
        setSErr(error.message);
        setSBusy(false);
        return;
      }
      if (!data?.user) {
        setSErr('Could not create account. Please try again.');
        setSBusy(false);
        return;
      }

      setSDone(true);
      setAuthUser(data.user);
      if (onAuthChange) onAuthChange(data.user);
      saveParentEmail(sEmail.trim());
      setSBusy(false);
    } catch {
      setSErr('Connection error. Please try again.');
      setSBusy(false);
    }
  };

  const doGoogle = async () => {
    setGoogleBusy(true);
    try {
      localStorage.setItem('rq:oauthPending', step);
      localStorage.setItem('rq:onboardingStep', step);
    } catch {
      // noop
    }
    const r = await supaSignInWithGoogle();
    if (r?.error) {
      setSErr(r.error.message || 'Google sign-in unavailable.');
      setGoogleBusy(false);
    }
  };

  const doTrial = async () => {
    let emailForStripe = sEmail;
    if (!emailForStripe && authUser?.email) emailForStripe = authUser.email;
    if (!emailForStripe) {
      try {
        const session = await supaGetSession();
        if (session?.user?.email) emailForStripe = session.user.email;
      } catch {
        // noop
      }
    }

    if (!emailForStripe || !emailForStripe.includes('@')) {
      setStripeErr('Please sign in first so we can verify parent consent.');
      return;
    }

    if (!stripeEl || !stripeCardRef.current) {
      setStripeErr('Card entry failed to load. Please refresh and try again.');
      return;
    }

    setStripeLoading(true);
    setStripeErr('');
    try {
      const { paymentMethod, error } = await stripeEl.createPaymentMethod({
        type: 'card',
        card: stripeCardRef.current,
      });
      if (error) {
        setStripeErr(error.message);
        setStripeLoading(false);
        return;
      }

      const res = await fetch('/api/stripe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'create-trial',
          email: emailForStripe.trim(),
          paymentMethodId: paymentMethod.id,
          plan: 'starter',
        }),
      });
      const data = await res.json();
      if (data.error) {
        setStripeErr(data.error);
        setStripeLoading(false);
        return;
      }

      if (data.ok) {
        try {
          localStorage.setItem('rq:trialActive', 'true');
          localStorage.setItem('rq:trialEnd', String(data.trialEnd));
          localStorage.setItem('rq:stripeCustomer', data.customerId);
          localStorage.setItem('rq:hasVPC', 'true');
        } catch {
          // noop
        }
      }

      setStripeLoading(false);
      setStep(STEPS.CHILD_PROFILE);
    } catch {
      setStripeErr('Connection error. Please try again.');
      setStripeLoading(false);
    }
  };

  const addPinDigit = (d) => {
    const activePin = confirmStep ? confirmPin : pinSetup;
    if (activePin.length >= 4) return;

    if (confirmStep) {
      const next = confirmPin + d;
      setConfirmPin(next);
      if (pinError) setPinError('');
      if (next.length === 4) {
        if (next !== pinSetup) {
          setPinError("PINs don't match — try again");
          setTimeout(() => setConfirmPin(''), 120);
          return;
        }
        updateData({ pin: next });
      }
      return;
    }

    const next = pinSetup + d;
    setPinSetup(next);
    if (pinError) setPinError('');
    if (next.length === 4) {
      setConfirmStep(true);
      setConfirmPin('');
    }
  };

  const clearPinDigit = () => {
    if (confirmStep) setConfirmPin((p) => p.slice(0, -1));
    else setPinSetup((p) => p.slice(0, -1));
    if (pinError) setPinError('');
  };

  const finishOnboarding = () => {
    const resolvedAge = onboardingData.childAge || AGE_BRACKET_TO_AGE[onboardingData.ageBracket] || 7;
    const resolvedPin = onboardingData.pin || pinSetup || null;
    onComplete({
      cn: onboardingData.childName.trim(),
      ca: resolvedAge,
      ints: onboardingData.interests,
      conf: onboardingData.concerns,
      focus: onboardingData.concerns[0] || '',
      goal: onboardingData.goal,
      heroName: onboardingData.heroName,
      ageBracket: onboardingData.ageBracket,
      pin: resolvedPin,
      tmr: 15,
    });
  };

  let content = null;

  if (!authChecked && step === STEPS.AUTH_PAYMENT) {
    content = (
      <div className="px-6 py-16 text-center">
        <div className="mx-auto h-10 w-10 rounded-full border-4 border-finley-text-muted/20 border-t-finley-primary animate-spin" />
      </div>
    );
  } else if (step === STEPS.WELCOME) {
    content = (
      <motion.div key={step} {...screenTransition} style={{ maxWidth: 460, margin: '0 auto', padding: '24px 20px', textAlign: 'center' }}>
        <img
          src={finleyLogo}
          alt="Finley"
          style={{
            width: 280,
            height: 280,
            borderRadius: '50%',
            objectFit: 'cover',
            margin: '0 auto 16px',
            display: 'block',
          }}
        />
        <div style={{ marginTop: 18, fontFamily: F, fontSize: 36, color: NAVY, lineHeight: 1.1 }}>
          Reading clicks when it's personal.
        </div>
        <p style={{ marginTop: 12, color: 'rgba(15,43,60,0.72)', fontFamily: N, fontSize: 17, lineHeight: 1.5 }}>
          Finley adapts to your child's unique reading level — no two readers are the same.
        </p>
        <div style={{ marginTop: 30 }}>
          <_NBtn full onClick={() => setStep(STEPS.AGE)}>
            Let's Get Started
          </_NBtn>
        </div>
      </motion.div>
    );
  } else if (step === STEPS.AGE) {
    content = (
      <motion.div key={step} {...screenTransition} style={{ maxWidth: 460, margin: '0 auto', padding: '24px 20px' }}>
        <h2 style={{ fontFamily: F, color: NAVY, fontSize: 27, textAlign: 'center', marginBottom: 6 }}>How old is your child?</h2>
        <p style={{ fontFamily: N, color: 'rgba(15,43,60,0.72)', textAlign: 'center', marginBottom: 18 }}>
          This helps us show you the right reading milestones.
        </p>
        <div style={{ display: 'grid', gap: 10, marginBottom: 22 }}>
          {AGE_OPTIONS.map((opt) => {
            const sel = onboardingData.ageBracket === opt.id;
            return (
              <motion.button
                key={opt.id}
                whileTap={{ scale: 0.97 }}
                onClick={() => updateData({ ageBracket: opt.id, childAge: AGE_BRACKET_TO_AGE[opt.id] })}
                onPointerDown={_btnPress}
                onPointerUp={_btnRelease}
                onPointerLeave={_btnRelease}
                style={{
                  textAlign: 'left',
                  borderRadius: 16,
                  border: `2px solid ${sel ? TEAL : '#E5EBF0'}`,
                  background: sel ? 'rgba(13,148,136,0.15)' : '#FFFFFF',
                  color: NAVY,
                  padding: '14px 16px',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
              >
                <div style={{ fontFamily: F, fontSize: 18 }}>{opt.label}</div>
                <div style={{ fontFamily: N, fontSize: 14, color: 'rgba(15,43,60,0.65)' }}>{opt.detail}</div>
              </motion.button>
            );
          })}
        </div>
        <_NBtn full disabled={!onboardingData.ageBracket} onClick={() => setStep(STEPS.READING_CHECK)}>
          Continue
        </_NBtn>
      </motion.div>
    );
  } else if (step === STEPS.READING_CHECK) {
    content = (
      <motion.div key={step} {...screenTransition}>
        <ReadingStageCheck
          ageBracket={onboardingData.ageBracket}
          onComplete={({ readingChecks, readingStage }) => {
            updateData({ readingChecks, readingStage });
            setStep(STEPS.CONCERNS_GOALS);
          }}
        />
      </motion.div>
    );
  } else if (step === STEPS.CONCERNS_GOALS) {
    content = (
      <motion.div key={step} {...screenTransition} style={{ maxWidth: 460, margin: '0 auto', padding: '24px 20px' }}>
        <h2 style={{ fontFamily: F, color: NAVY, fontSize: 27, marginBottom: 8 }}>What brings you to Finley?</h2>
        <div style={{ display: 'grid', gap: 8, marginBottom: 16 }}>
          {CONCERN_OPTIONS.map((item) => {
            const sel = onboardingData.concerns.includes(item);
            return (
              <motion.button
                key={item}
                whileTap={{ scale: 0.97 }}
                onClick={() => toggleConcern(item)}
                style={{
                  borderRadius: 14,
                  border: `2px solid ${sel ? TEAL : '#E5EBF0'}`,
                  background: sel ? 'rgba(13,148,136,0.15)' : '#FFFFFF',
                  color: NAVY,
                  padding: '10px 14px',
                  fontFamily: N,
                  fontSize: 15,
                  textAlign: 'left',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
              >
                {item}
              </motion.button>
            );
          })}
        </div>

        <div style={{ height: 1, background: '#E5EBF0', margin: '18px 0' }} />

        <h3 style={{ fontFamily: F, color: NAVY, fontSize: 22, marginBottom: 8 }}>What's your #1 reading goal?</h3>
        <div style={{ display: 'grid', gap: 8, marginBottom: 22 }}>
          {GOAL_OPTIONS.map((goal) => {
            const sel = onboardingData.goal === goal;
            return (
              <motion.button
                key={goal}
                whileTap={{ scale: 0.97 }}
                onClick={() => updateData({ goal })}
                style={{
                  borderRadius: 14,
                  border: `2px solid ${sel ? TEAL : '#E5EBF0'}`,
                  background: sel ? 'rgba(13,148,136,0.15)' : '#FFFFFF',
                  color: NAVY,
                  padding: '12px 14px',
                  fontFamily: N,
                  fontSize: 16,
                  textAlign: 'left',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
              >
                {goal}
              </motion.button>
            );
          })}
        </div>

        <_NBtn
          full
          disabled={onboardingData.concerns.length === 0 || !onboardingData.goal}
          onClick={() => setStep(STEPS.PREVIEW)}
        >
          Continue
        </_NBtn>
      </motion.div>
    );
  } else if (step === STEPS.PREVIEW) {
    content = (
      <motion.div key={step} {...screenTransition}>
        <PersonalizedPreview onboardingData={onboardingData} onContinue={() => setStep(STEPS.AUTH_PAYMENT)} />
      </motion.div>
    );
  } else if (step === STEPS.AUTH_PAYMENT) {
    content = (
      <motion.div key={step} {...screenTransition} style={{ maxWidth: 460, margin: '0 auto', padding: '24px 20px' }}>
        <h2 style={{ fontFamily: F, color: NAVY, fontSize: 27, lineHeight: 1.2, textAlign: 'center', marginBottom: 6 }}>
          Start your 30-day free trial
        </h2>
        <p style={{ fontFamily: N, color: 'rgba(15,43,60,0.72)', textAlign: 'center', marginBottom: 16 }}>
          $0 today. Cancel anytime. No commitment.
        </p>
        {isDevSkipEnabled ? (
          <button
            onClick={() => {
              try {
                localStorage.setItem('rq:hasVPC', 'true');
                localStorage.setItem('rq:stripeCustomer', 'dev-skip');
              } catch {
                // noop
              }
              setStep(STEPS.CHILD_PROFILE);
            }}
            style={{
              width: '100%',
              borderRadius: 12,
              border: `2px dashed ${C.coral || C.primary}`,
              background: 'rgba(232, 97, 77, 0.14)',
              color: C.coral || C.primary,
              padding: '12px 14px',
              fontFamily: F,
              fontSize: 14,
              marginBottom: 12,
              cursor: 'pointer',
            }}
          >
            DEV ONLY: Skip Auth + Card (REMOVE BEFORE COMMIT)
          </button>
        ) : null}

        {!isAuthed ? (
          <div
            style={{
              background: WARM_WHITE,
              borderRadius: 16,
              padding: 24,
              boxShadow: '0 4px 12px rgba(15,43,60,0.08)',
              marginBottom: 14,
            }}
          >
            <button
              onClick={doGoogle}
              disabled={googleBusy}
              style={{
                width: '100%',
                minHeight: 52,
                borderRadius: 12,
                border: '2px solid #E5EBF0',
                background: '#fff',
                color: NAVY,
                fontFamily: N,
                fontWeight: 700,
                fontSize: 15,
                marginBottom: 10,
                cursor: googleBusy ? 'not-allowed' : 'pointer',
                opacity: googleBusy ? 0.7 : 1,
                transition: 'all 0.2s ease',
                animation: googleBusy ? 'rqPulseGlow 1.4s ease-in-out infinite' : 'none',
              }}
            >
              {googleBusy ? 'Connecting...' : 'Continue with Google'}
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <div style={{ flex: 1, height: 1, background: '#E5EBF0' }} />
              <span style={{ fontFamily: N, color: 'rgba(15,43,60,0.6)', fontSize: 13 }}>or</span>
              <div style={{ flex: 1, height: 1, background: '#E5EBF0' }} />
            </div>

            <div style={{ display: 'grid', gap: 8 }}>
              <input
                type="email"
                value={sEmail}
                onChange={(e) => {
                  setSEmail(e.target.value);
                  setSErr('');
                }}
                placeholder="Email"
                autoComplete="email"
                onFocus={() => setEmailFocused(true)}
                onBlur={() => setEmailFocused(false)}
                style={{
                  width: '100%',
                  height: 48,
                  borderRadius: 12,
                  border: `2px solid ${emailFocused ? TEAL : '#E5EBF0'}`,
                  padding: '0 12px',
                  fontFamily: N,
                  fontSize: 15,
                  color: NAVY,
                  outline: 'none',
                  transition: 'border-color 0.2s ease',
                }}
              />
              <input
                type="password"
                value={sPw}
                onChange={(e) => {
                  setSPw(e.target.value);
                  setSErr('');
                }}
                placeholder="Password"
                autoComplete="new-password"
                onFocus={() => setPwFocused(true)}
                onBlur={() => setPwFocused(false)}
                style={{
                  width: '100%',
                  height: 48,
                  borderRadius: 12,
                  border: `2px solid ${pwFocused ? TEAL : '#E5EBF0'}`,
                  padding: '0 12px',
                  fontFamily: N,
                  fontSize: 15,
                  color: NAVY,
                  outline: 'none',
                  transition: 'border-color 0.2s ease',
                }}
              />
            </div>

            {sErr ? <div style={{ marginTop: 8, color: C.danger, fontFamily: N, fontSize: 14 }}>{sErr}</div> : null}
            {sDone ? <div style={{ marginTop: 8, color: C.success, fontFamily: N, fontSize: 14 }}>Account ready. Continue below.</div> : null}

            <button
              onClick={doCreate}
              disabled={sBusy}
              style={{
                marginTop: 12,
                width: '100%',
                borderRadius: 12,
                border: 'none',
                background: TEAL,
                color: '#fff',
                fontFamily: F,
                fontSize: 16,
                padding: '14px 18px',
                opacity: sBusy ? 0.5 : 1,
                cursor: sBusy ? 'not-allowed' : 'pointer',
              }}
            >
              {sBusy ? 'Creating account...' : 'Create account'}
            </button>
          </div>
        ) : (
          <div
            style={{
              background: 'rgba(13,148,136,0.15)',
              border: `2px solid ${TEAL}`,
              color: TEAL,
              borderRadius: 12,
              padding: 12,
              marginBottom: 14,
              fontFamily: N,
              fontSize: 14,
            }}
          >
            Signed in as {authUser?.email || sEmail}
          </div>
        )}

        <div
          style={{
            background: WARM_WHITE,
            borderRadius: 16,
            padding: 24,
            boxShadow: '0 4px 12px rgba(15,43,60,0.08)',
            marginBottom: 14,
          }}
        >
          <div style={{ fontFamily: N, color: 'rgba(15,43,60,0.6)', fontSize: 12, letterSpacing: 1, marginBottom: 10 }}>
            PAYMENT INFORMATION
          </div>

          <div
            style={{
              borderRadius: 12,
              border: '2px solid #E5EBF0',
              background: '#fff',
              padding: '12px 14px',
              marginBottom: 12,
            }}
          >
            <div ref={cardRef} style={{ minHeight: 24 }} />
            {!stripeReady ? <div style={{ fontFamily: N, color: 'rgba(15,43,60,0.6)', fontSize: 13 }}>Loading secure card form...</div> : null}
          </div>

          <div
            style={{
              borderRadius: 12,
              border: '1px solid #E5EBF0',
              background: '#fff',
              padding: '12px 14px',
              fontFamily: N,
              color: NAVY,
              fontSize: 14,
              lineHeight: 1.5,
            }}
          >
            🔒 Your payment info verifies you're a parent or guardian — required by children's privacy law. We take your
            child's privacy seriously.
          </div>
        </div>

        {stripeErr ? <div style={{ marginBottom: 10, color: C.danger, fontFamily: N, fontSize: 14 }}>{stripeErr}</div> : null}

        <button
          onClick={doTrial}
          disabled={stripeLoading}
          style={{
            background: TEAL,
            color: '#fff',
            borderRadius: 12,
            padding: '16px 32px',
            fontFamily: F,
            fontSize: 16,
            border: 'none',
            width: '100%',
            opacity: stripeLoading ? 0.5 : 1,
            cursor: stripeLoading ? 'not-allowed' : 'pointer',
          }}
        >
          {stripeLoading ? 'Processing...' : 'Start Free Trial'}
        </button>

        <p style={{ marginTop: 12, fontFamily: N, color: 'rgba(15,43,60,0.72)', fontSize: 13, textAlign: 'center', lineHeight: 1.5 }}>
          By starting, you agree to our{' '}
          <a href="https://finleyreads.com/terms.html" target="_blank" rel="noopener" style={{ color: NAVY, textDecoration: 'underline' }}>
            Terms of Service
          </a>{' '}
          and{' '}
          <a href="https://finleyreads.com/privacy.html" target="_blank" rel="noopener" style={{ color: NAVY, textDecoration: 'underline' }}>
            Privacy Policy
          </a>
          .
        </p>
      </motion.div>
    );
  } else if (step === STEPS.CHILD_PROFILE) {
    content = (
      <motion.div key={step} {...screenTransition} style={{ maxWidth: 460, margin: '0 auto', padding: '24px 20px' }}>
        <h2 style={{ fontFamily: F, color: NAVY, fontSize: 28, marginBottom: 14, textAlign: 'center' }}>Now let's meet your reader!</h2>
        <input
          type="text"
          value={onboardingData.childName}
          onChange={(e) => updateData({ childName: e.target.value })}
          onFocus={() => setChildNameFocused(true)}
          onBlur={() => setChildNameFocused(false)}
          maxLength={20}
          placeholder="Child's name or nickname"
          autoComplete="off"
          style={{
            width: '100%',
            height: 52,
            borderRadius: 12,
            border: `2px solid ${childNameFocused ? TEAL : '#E5EBF0'}`,
            padding: '0 12px',
            fontFamily: N,
            fontSize: 16,
            marginBottom: 10,
            color: NAVY,
            background: '#fff',
            outline: 'none',
            transition: 'border-color 0.2s ease',
          }}
        />
        <div style={{ fontFamily: N, color: 'rgba(15,43,60,0.72)', fontSize: 14, marginBottom: 14 }}>
          We only use their name in-app. AI systems never receive your child's real name.
        </div>

        <label style={{ fontFamily: N, color: NAVY, fontSize: 14, display: 'block', marginBottom: 8 }}>Age</label>
        <select
          value={onboardingData.childAge || ''}
          onChange={(e) => updateData({ childAge: Number(e.target.value) || null })}
          onFocus={() => setChildAgeFocused(true)}
          onBlur={() => setChildAgeFocused(false)}
          aria-label="Child age"
          style={{
            width: '100%',
            height: 52,
            borderRadius: 12,
            border: `2px solid ${childAgeFocused ? TEAL : '#E5EBF0'}`,
            padding: '0 12px',
            fontFamily: N,
            fontSize: 16,
            marginBottom: 22,
            color: NAVY,
            background: '#fff',
            outline: 'none',
            transition: 'border-color 0.2s ease',
          }}
        >
          <option value="">Select age</option>
          {[4, 5, 6, 7, 8, 9, 10, 11, 12].map((age) => (
            <option key={age} value={age}>{age}</option>
          ))}
        </select>

        <_NBtn
          full
          disabled={!onboardingData.childName.trim() || !(onboardingData.childAge >= 4 && onboardingData.childAge <= 12)}
          onClick={() => setStep(STEPS.HERO_NAME)}
        >
          Continue
        </_NBtn>
      </motion.div>
    );
  } else if (step === STEPS.HERO_NAME) {
    const selectedHero = HEROES.find((hero) => hero.id === onboardingData.heroName);

    content = (
      <motion.div key={step} {...screenTransition} style={{ maxWidth: 460, margin: '0 auto', padding: '24px 20px' }}>
        <h2 style={{ fontFamily: F, color: NAVY, fontSize: 26, marginBottom: 6, textAlign: 'center' }}>Choose a reading hero!</h2>
        <p style={{ fontFamily: N, color: 'rgba(15,43,60,0.72)', textAlign: 'center', marginBottom: 16 }}>
          This character stars in your child's stories and goes on reading adventures with them.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 16 }}>
          {HEROES.map((hero) => {
            const selected = onboardingData.heroName === hero.id;
            return (
              <HeroFlipCard
                key={hero.id}
                hero={hero}
                selected={selected}
                onSelect={(id) => updateData({ heroName: id })}
              />
            );
          })}
        </div>

        {selectedHero ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            style={{
              textAlign: 'center',
              marginBottom: 14,
              padding: '10px 16px',
              background: selectedHero.bgGradient,
              borderRadius: 12,
              border: `2px solid ${selectedHero.color}`,
            }}
          >
            <span style={{ fontSize: 24 }}>{selectedHero.emoji}</span>
            <span
              style={{
                fontFamily: F,
                fontSize: 18,
                color: selectedHero.color,
                marginLeft: 8,
              }}
            >
              {onboardingData.heroName}
            </span>
          </motion.div>
        ) : null}

        <h3 style={{ fontFamily: F, color: NAVY, fontSize: 21, marginBottom: 4 }}>Choose adventure themes</h3>
        <p style={{ fontFamily: N, color: 'rgba(15,43,60,0.72)', marginBottom: 10 }}>
          Tap to explore — these shape the stories your hero goes on. (pick up to 3)
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, marginBottom: 22 }}>
          {INTEREST_OPTIONS.map((item) => {
            const sel = onboardingData.interests.includes(item.id);
            return (
              <FlipCard
                key={item.id}
                item={item}
                selected={sel}
                onToggle={toggleInterest}
              />
            );
          })}
        </div>

        <_NBtn
          full
          disabled={!onboardingData.heroName || onboardingData.interests.length === 0}
          onClick={() => setStep(STEPS.PIN)}
        >
          That's My Hero!
        </_NBtn>
      </motion.div>
    );
  } else if (step === STEPS.PIN) {
    const activePin = confirmStep ? confirmPin : pinSetup;
    const complete = !!onboardingData.pin;

    content = (
      <motion.div key={step} {...screenTransition} style={{ maxWidth: 460, margin: '0 auto', padding: '24px 20px', textAlign: 'center' }}>
        <h2 style={{ fontFamily: F, color: NAVY, fontSize: 28, marginBottom: 6 }}>
          {confirmStep ? 'Confirm your PIN' : 'Set a parent PIN'}
        </h2>
        <p style={{ fontFamily: N, color: 'rgba(15,43,60,0.72)', marginBottom: 16 }}>This 4-digit PIN protects settings and account access.</p>

        <div style={{ display: 'flex', justifyContent: 'center', gap: 10, marginBottom: 18 }}>
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              style={{
                width: 16,
                height: 16,
                borderRadius: 999,
                background: activePin.length > i ? TEAL : '#D8DEE4',
              }}
            />
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, maxWidth: 220, margin: '0 auto 10px' }}>
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, null, 0, '⌫'].map((d, i) => (
            <button
              key={i}
              disabled={d === null}
              onClick={() => {
                if (d === '⌫') clearPinDigit();
                else if (d !== null) addPinDigit(String(d));
              }}
              style={{
                height: 48,
                borderRadius: 12,
                border: '2px solid #E5EBF0',
                background: d === null ? 'transparent' : '#FFFFFF',
                color: NAVY,
                fontFamily: F,
                fontSize: 20,
                cursor: d === null ? 'default' : 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              {d}
            </button>
          ))}
        </div>

        {pinError ? <div style={{ color: C.danger, fontFamily: N, fontSize: 14 }}>{pinError}</div> : null}

        <div style={{ marginTop: 14 }}>
          <_NBtn full disabled={!complete} onClick={finishOnboarding}>Continue</_NBtn>
        </div>
      </motion.div>
    );
  }

  return (
    <div
      ref={containerRef}
      style={{ minHeight: '100%', paddingTop: step !== STEPS.WELCOME ? 28 : 6, overflowY: 'auto', position: 'relative' }}
    >
      {step !== STEPS.WELCOME ? (
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'rgba(15, 43, 60, 0.12)', overflow: 'hidden' }}>
          <div
            style={{
              width: `${progressPct}%`,
              height: '100%',
              background: TEAL,
              transition: 'width 0.4s ease',
            }}
          />
        </div>
      ) : null}

      {step !== STEPS.WELCOME ? (
        <button
          onClick={goBack}
          style={{
            position: 'absolute',
            top: 16,
            left: 16,
            background: 'rgba(15, 43, 60, 0.08)',
            border: 'none',
            borderRadius: '50%',
            width: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: 'rgba(15, 43, 60, 0.7)',
            fontSize: 20,
          }}
          aria-label="Go back"
        >
          ←
        </button>
      ) : null}

      <AnimatePresence mode="wait" initial={false}>
        {content}
      </AnimatePresence>
    </div>
  );
}
